import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Leaf } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const { loginWithCreds } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithCreds(email, password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] mx-auto max-w-md px-6 py-16" data-testid="login-page">
      <div className="flex items-center gap-2 mb-6 justify-center">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#2C3E2D] text-[#F9F8F6]">
          <Leaf className="h-4 w-4" />
        </span>
        <span className="font-heading text-xl text-[#2C3E2D]">Gramo<span className="text-[#E07A5F]">.</span>in</span>
      </div>
      <h1 className="font-heading text-3xl text-[#2C3E2D] text-center font-medium">Welcome back to Gramo</h1>
      <p className="text-sm text-[#6B7B6C] text-center mt-2">Sign in to check out and track your orders.</p>

      <button
        onClick={handleGoogle}
        className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full border border-[#E5E5E0] bg-white h-11 text-sm font-medium hover:border-[#81B29A] transition-colors"
        data-testid="login-google-btn"
      >
        <GoogleIcon /> Continue with Google
      </button>

      <div className="flex items-center gap-3 my-6">
        <div className="h-px flex-1 bg-[#E5E5E0]" />
        <span className="text-xs uppercase tracking-widest text-[#6B7B6C]">or</span>
        <div className="h-px flex-1 bg-[#E5E5E0]" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email address" className="h-11 rounded-xl bg-white" data-testid="login-email" />
        <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="Password" className="h-11 rounded-xl bg-white" data-testid="login-password" />
        <Button type="submit" disabled={loading} className="btn-primary rounded-full w-full h-11" data-testid="login-submit">
          {loading ? "Signing in…" : "Sign In"}
        </Button>
      </form>
      <p className="text-sm text-center text-[#6B7B6C] mt-6">
        New to Gramo? <Link to="/signup" className="text-[#2C3E2D] underline underline-offset-2" data-testid="link-to-signup">Create an account</Link>
      </p>
    </div>
  );
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export default Login;
